"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Loader2, UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { updateTenantProfile } from "@/services/api";
import type { TenantSummary } from "@/services/api";

type Props = {
  summary: TenantSummary | null;
  loading: boolean;
  onSaved: () => Promise<void>;
};

export function TenantOrganizationCard({ summary, loading, onSaved }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!summary) {
      return;
    }
    setName(summary.name);
    setCompanyAddress(summary.companyAddress ?? "");
    setContactName(summary.contactName ?? "");
    setContactEmail(summary.contactEmail ?? "");
    setContactPhone(summary.contactPhone ?? "");
  }, [summary]);

  useEffect(() => {
    if (!success) {
      return;
    }
    const t = window.setTimeout(() => setSuccess(false), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!summary) {
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateTenantProfile({
        name: name.trim() || summary.name,
        companyAddress: companyAddress.trim() || null,
        contactName: contactName.trim() || null,
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
      });
      setSuccess(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(t("dashboard.organization.saveError")));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !summary) {
    return null;
  }
  if (!summary) {
    return null;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-border/50 bg-card p-5 shadow-sm space-y-6"
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary" />
          {String(t("dashboard.organization.title"))}
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          {String(t("dashboard.organization.subtitle"))}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="org-name">{String(t("dashboard.organization.companyName"))}</Label>
          <Input
            id="org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            required
            className="max-w-xl"
          />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="org-address">{String(t("dashboard.organization.companyAddress"))}</Label>
          <Textarea
            id="org-address"
            value={companyAddress}
            onChange={(e) => setCompanyAddress(e.target.value)}
            rows={3}
            className="max-w-2xl resize-y min-h-[72px]"
            placeholder={String(t("dashboard.organization.addressPlaceholder"))}
          />
        </div>
      </div>

      <div>
        <h3 className="text-xs font-medium text-muted-foreground flex items-center gap-2 mb-3">
          <UserCircle2 className="h-3.5 w-3.5" />
          {String(t("dashboard.organization.contactTitle"))}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="contact-name">{String(t("dashboard.organization.contactName"))}</Label>
            <Input
              id="contact-name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              maxLength={255}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-email">{String(t("dashboard.organization.contactEmail"))}</Label>
            <Input
              id="contact-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              maxLength={255}
            />
          </div>
          <div className="space-y-2 sm:col-span-2 lg:col-span-1">
            <Label htmlFor="contact-phone">{String(t("dashboard.organization.contactPhone"))}</Label>
            <Input
              id="contact-phone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              maxLength={64}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {error}
        </div>
      )}
      {success && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400" role="status">
          {String(t("dashboard.organization.saveSuccess"))}
        </p>
      )}

      <div>
        <Button type="submit" size="sm" disabled={saving || !name.trim()}>
          {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          {String(t("dashboard.organization.save"))}
        </Button>
      </div>
    </form>
  );
}
